import { postCommunication, communicationError, deliveryLabel } from '../../lib/communicationRequests';
import { SMSMessagePreview } from './SMSMessagePreview';
import { PhoneNumberSelector } from './PhoneNumberSelector';
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Textarea from '@mui/joy/Textarea';
import CircularProgress from '@mui/joy/CircularProgress';
import { PaperPlaneTilt as SendIcon } from '@phosphor-icons/react';
import { format } from 'date-fns';
import type { InboxItem, SMSMessage } from '../../types/inbox';
import { getSmsPhoneNumber, getSmsLocalNumber } from './inboxItemUtils';
import api from '../../lib/api';
import useWebSocket from '../../hooks/useWebSocket';

interface InlineSMSViewProps {
  item: InboxItem;
  onReply?: (content: string, type: 'email' | 'sms') => void;
  onReplyStateChange?: (isReplying: boolean) => void;
}

/** Same last-10-digits comparison the contact timeline uses. */
const isSamePhoneNumber = (a?: string | null, b?: string | null): boolean => {
  if (!a || !b) return false;
  const digitsA = a.replace(/\D/g, '').slice(-10);
  const digitsB = b.replace(/\D/g, '').slice(-10);
  return digitsA.length === 10 && digitsA === digitsB;
};

const messageTime = (message: SMSMessage): string => {
  const stamp = message.sent_at || message.created_at;
  if (!stamp) return '';
  const parsed = new Date(stamp);
  return Number.isNaN(parsed.getTime()) ? '' : format(parsed, 'MMM d, h:mm a');
};

const InlineSMSView: React.FC<InlineSMSViewProps> = ({ item, onReplyStateChange }) => {
  const [replyContent, setReplyContent] = useState('');
  const [messages, setMessages] = useState<SMSMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const phoneNumber = getSmsPhoneNumber(item);
  const localNumber = getSmsLocalNumber(item);
  const [sender, setSender] = useState(localNumber || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setSender(localNumber || ''); setError(''); setReplyContent(''); }, [item.id, localNumber]);
  const { subscribe } = useWebSocket({ autoConnect: true });

  // Notify parent when reply content changes
  React.useEffect(() => {
    if (onReplyStateChange) {
      onReplyStateChange(replyContent.length > 0);
    }
  }, [replyContent, onReplyStateChange]);

  const loadMessages = useCallback(async () => {
    if (!phoneNumber) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get<{ results: SMSMessage[] }>(
        `/api/inbox/sms/?phone=${encodeURIComponent(phoneNumber)}${localNumber ? `&local_number=${encodeURIComponent(localNumber)}` : ""}`
      );
      const results = response.data.results ?? [];
      setMessages(
        [...results].sort((a, b) =>
          new Date(a.sent_at || a.created_at).getTime() -
          new Date(b.sent_at || b.created_at).getTime()
        )
      );
    } catch {
      // Leave the thread empty rather than showing invented messages
    } finally {
      setLoading(false);
    }
  }, [phoneNumber, localNumber]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Live updates: a reply arriving (sms_received) and a delivery receipt
  // flipping the status (sms_updated) both land without reopening the row.
  useEffect(() => {
    const unsubReceived = subscribe('sms_received', (payload: { from_number?: string }) => {
      if (isSamePhoneNumber(payload.from_number, phoneNumber)) {
        loadMessages();
      }
    });
    const unsubUpdated = subscribe('sms_updated', (payload: SMSMessage) => {
      setMessages((prev) =>
        prev.map((message) => (message.id === payload.id ? { ...message, ...payload } : message))
      );
    });
    return () => {
      unsubReceived();
      unsubUpdated();
    };
  }, [subscribe, phoneNumber, loadMessages]);

  const handleSendReply = async () => {
    if (!replyContent.trim() || sending || !sender || !phoneNumber) return;
    setSending(true); setError('');
    try {
      await postCommunication('/api/inbox/sms/send/', { to_number: phoneNumber, from_number: sender, body: replyContent, purpose: 'conversation' });
      setReplyContent(''); await loadMessages();
    } catch (error) { setError(communicationError(error)); }
    finally { setSending(false); }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Conversation Header */}
      <Box sx={{ mb: 2 }}>
        <Typography level="body-sm" fontWeight="md">
          SMS Conversation with {item.lead_name || phoneNumber || 'Unknown'}
        </Typography>
      </Box>

      {/* Messages - scrollable */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: '200px',
          mb: 2,
          pr: 1,
          bgcolor: 'background.level1',
          borderRadius: 'sm',
          p: 1,
        }}
      >
        {loading ? (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size="sm" />
          </Stack>
        ) : messages.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'text.secondary', py: 1 }}>
            No messages in this conversation yet.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {messages.map((message) => {
              const isOutbound = message.direction === 'outbound';
              return (
                <Box
                  key={message.id}
                  sx={{
                    alignSelf: isOutbound ? 'flex-end' : 'flex-start',
                    maxWidth: '70%',
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: isOutbound ? 'primary.500' : 'background.level2',
                      color: isOutbound ? 'white' : 'text.primary',
                      borderRadius: 'md',
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Typography
                      level="body-sm"
                      sx={{ color: 'inherit', whiteSpace: 'pre-wrap' }}
                    >
                      {message.body}
                    </Typography>
                  </Box>
                  <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5, px: 1 }}>
                    {messageTime(message)} {isOutbound && ` · ${deliveryLabel(message.status)}`}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Reply Input */}
      <Stack spacing={2}>
        {!localNumber && <PhoneNumberSelector smsOnly value={sender} onChange={setSender} />}
        <Textarea
          placeholder="Type your message..."
          value={replyContent}
          onChange={(e) => setReplyContent(e.target.value)}
          minRows={2}
          maxRows={4}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendReply();
            }
          }}
        />
        <SMSMessagePreview toNumber={phoneNumber || ''} fromNumber={sender} body={replyContent} />
        {error && <Typography role="alert" color="danger">{error}</Typography>}
        <Button
          variant="solid"
          size="sm"
          endDecorator={<SendIcon />}
          onClick={handleSendReply}
          loading={sending}
          disabled={!replyContent.trim() || !phoneNumber || !sender || sending}
          sx={{ alignSelf: 'flex-end' }}
        >
          Send SMS
        </Button>
      </Stack>
    </Box>
  );
};

export default InlineSMSView;
